import { useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';

// material-ui
import { useTheme } from '@mui/material/styles';
import { Alert, Box, Button, Toolbar, useMediaQuery } from '@mui/material';

// project import
import Drawer from './Drawer';
import Header from './Header';
import navigation from 'menu-items';
import Breadcrumbs from 'components/@extended/Breadcrumbs';
import { useGiraf } from 'giraf';

// types
import { openDrawer } from 'store/reducers/menu';

const BASE = process.env.REACT_APP_API_URL;

// ==============================|| MAIN LAYOUT ||============================== //

const MainLayout = () => {
  const theme = useTheme();
  const matchDownLG = useMediaQuery(theme.breakpoints.down('lg'));
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { gHead } = useGiraf();

  const hideBreadcrumb = ['/communications/compose', '/communications/whatsapp/numbers', '/communications/overview', '/ground-operations/agent-network', '/ground-operations/agent-task', '/agent-activity', '/elections/poll-results', '/elections/tally-results', '/elections/reported-incidents', '/finance/wallet', '/finance/budget-utilization', '/account/settings'].some(p => location.pathname.startsWith(p));

  const { drawerOpen } = useSelector((state) => state.menu);

  const [open, setOpen] = useState(drawerOpen);
  const [subStatus, setSubStatus] = useState(null); // { status, daysLeft, plan }

  const handleDrawerToggle = () => {
    setOpen(!open);
    dispatch(openDrawer({ drawerOpen: !open }));
  };

  useEffect(() => {
    setOpen(!matchDownLG);
    dispatch(openDrawer({ drawerOpen: !matchDownLG }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchDownLG]);

  useEffect(() => {
    if (open !== drawerOpen) setOpen(drawerOpen);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawerOpen]);

  // Fetch subscription status once on mount
  useEffect(() => {
    if (!gHead?.AuthToken) return;
    axios
      .get(`${BASE}/accounts/subscription/status`, { headers: { Authorization: gHead.AuthToken } })
      .then((r) => setSubStatus(r.data))
      .catch(() => {}); // silent — don't break layout on error
  }, [gHead]);

  const trialWarning =
    subStatus?.status === 'trial' && subStatus.daysLeft !== null && subStatus.daysLeft <= 7;
  const trialExpired = subStatus?.status === 'expired' || subStatus?.status === 'cancelled';

  return (
    <Box sx={{ display: 'flex', width: '100%' }}>
      <Header open={open} handleDrawerToggle={handleDrawerToggle} />
      <Drawer open={open} handleDrawerToggle={handleDrawerToggle} />
      <Box component="main" sx={{ width: '100%', flexGrow: 1, p: { xs: 2, sm: 3 } }}>
        <Toolbar />

        {/* Trial expiry banner */}
        {trialExpired && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            action={
              <Button color="inherit" size="small" onClick={() => navigate('/settings')}>
                Upgrade Now
              </Button>
            }
          >
            Your free trial has expired. Upgrade your plan to continue using Siasa Digital.
          </Alert>
        )}
        {!trialExpired && trialWarning && (
          <Alert
            severity="warning"
            sx={{ mb: 2 }}
            action={
              <Button color="inherit" size="small" onClick={() => navigate('/settings')}>
                Upgrade
              </Button>
            }
          >
            Your free trial expires in <strong>{subStatus.daysLeft} day{subStatus.daysLeft !== 1 ? 's' : ''}</strong>. Upgrade to keep access.
          </Alert>
        )}

        {!hideBreadcrumb && <Breadcrumbs navigation={navigation} title />}
        <Outlet />
      </Box>
    </Box>
  );
};

export default MainLayout;
